import React from 'react';
import { MapPin, ExternalLink, Globe } from 'lucide-react';
import Layout from '../components/Layout';
import { useTranslation } from '../context/TranslationContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../data/seoMeta';

var distributors = [
  {
    name: 'TC Gakki',
    city: 'Tokyo, Japan',
    address: '169-0073 Tokyo, Shinjuku City, Hyakunincho, 1 Chome-11-23',
    url: 'https://tcgakki.com',
    flag: 'JP'
  },
  {
    name: 'SZ Music Shop',
    city: 'Mexico City, Mexico',
    address: 'C. Paris 188A, Del Carmen, Coyoacan, 04100 Ciudad de Mexico, CDMX',
    url: 'https://www.szmusicshop.com',
    flag: 'MX'
  },
  {
    name: 'Saxonicos Workshop',
    city: 'Mexico City, Mexico',
    address: 'Av. Division del Nte. 2943-Local 15, El Rosedal, Coyoacan, 04330 Ciudad de Mexico, CDMX',
    url: 'https://www.facebook.com/saxonicosworkshop/',
    flag: 'MX'
  },
  {
    name: "iik' - Winds Workshop",
    city: 'Playa del Carmen, Mexico',
    address: 'Playa del Carmen, Mexico, 77727',
    url: 'https://www.facebook.com/iikwindsworkshop',
    flag: 'MX'
  },
  {
    name: 'Taller de Reparacion De Instrumentos Domenzain',
    city: 'Mexico City, Mexico',
    address: 'Pensador Mexicano 19, Centro Historico de la Cdad. de Mexico, Guerrero, Cuauhtemoc, 06300 Ciudad de Mexico, CDMX',
    url: '',
    flag: 'MX'
  }
];

function DistributorCard(props) {
  var d = props.distributor;
  var Tag = d.url ? 'a' : 'div';
  var linkProps = d.url ? { href: d.url, target: '_blank', rel: 'noopener noreferrer' } : {};
  return React.createElement(Tag, Object.assign({
    'data-testid': 'distributor-card-' + d.name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
    className: 'block bg-neutral-900 border border-white/10 rounded-2xl p-8 hover:border-[#d4af37]/40 transition-all group'
  }, linkProps),
    React.createElement('div', { className: 'flex items-start justify-between mb-4' },
      React.createElement('div', null,
        React.createElement('h3', { className: 'text-xl font-bold text-white group-hover:text-[#d4af37] transition-colors' }, d.name),
        React.createElement('p', { className: 'text-[#d4af37] text-sm mt-1' }, d.city)
      ),
      d.url ? React.createElement(ExternalLink, { className: 'w-5 h-5 text-neutral-600 group-hover:text-[#d4af37] transition-colors flex-shrink-0 mt-1' }) : null
    ),
    React.createElement('div', { className: 'flex items-start gap-2 text-neutral-400 text-sm' },
      React.createElement(MapPin, { className: 'w-4 h-4 mt-0.5 flex-shrink-0' }),
      React.createElement('span', null, d.address)
    )
  );
}

function DistributorForm() {
  var t = useTranslation().t;
  var formState = React.useState({ name: '', email: '', company: '', location: '', message: '' });
  var form = formState[0];
  var setForm = formState[1];
  var submitState = React.useState({ loading: false, sent: false, error: '' });
  var status = submitState[0];
  var setStatus = submitState[1];

  function handleChange(field) {
    return function(e) {
      var updated = {};
      for (var k in form) updated[k] = form[k];
      updated[field] = e.target.value;
      setForm(updated);
    };
  }

  function handleSubmit(e) {
    e.preventDefault();
    setStatus({ loading: true, sent: false, error: '' });

    var API_URL = process.env.REACT_APP_BACKEND_URL;

    fetch(API_URL + '/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name,
        email: form.email,
        subject: 'Distributor Inquiry from ' + (form.company || form.name) + ' (' + form.location + ')',
        message: 'Company: ' + form.company + '\nLocation: ' + form.location + '\n\n' + form.message
      })
    })
      .then(function(res) {
        return res.text().then(function(text) {
          try { return JSON.parse(text); } catch(err) { return {}; }
        });
      })
      .then(function() {
        setStatus({ loading: false, sent: true, error: '' });
        setForm({ name: '', email: '', company: '', location: '', message: '' });
      })
      .catch(function(err) {
        setStatus({ loading: false, sent: false, error: err.message || 'Something went wrong' });
      });
  }

  if (status.sent) {
    return React.createElement('div', { className: 'text-center py-12', 'data-testid': 'distributor-form-success' },
      React.createElement('div', { className: 'w-16 h-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center mx-auto mb-4' },
        React.createElement(Globe, { className: 'w-8 h-8 text-[#d4af37]' })
      ),
      React.createElement('h3', { className: 'text-2xl font-bold text-white mb-2' }, t('distFormSuccessTitle')),
      React.createElement('p', { className: 'text-neutral-400' }, t('distFormSuccessBody'))
    );
  }

  var inputClass = 'w-full bg-neutral-900 border border-white/10 rounded-xl px-5 py-4 text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#d4af37]/50';

  return React.createElement('form', { onSubmit: handleSubmit, 'data-testid': 'distributor-form' },
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-4' },
      React.createElement('input', { type: 'text', placeholder: t('distFormName'), required: true, value: form.name, onChange: handleChange('name'), className: inputClass }),
      React.createElement('input', { type: 'email', placeholder: t('distFormEmail'), required: true, value: form.email, onChange: handleChange('email'), className: inputClass })
    ),
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4 mb-4' },
      React.createElement('input', { type: 'text', placeholder: t('distFormCompany'), value: form.company, onChange: handleChange('company'), className: inputClass }),
      React.createElement('input', { type: 'text', placeholder: t('distFormLocation'), required: true, value: form.location, onChange: handleChange('location'), className: inputClass })
    ),
    React.createElement('textarea', { placeholder: t('distFormMessage'), required: true, rows: 4, value: form.message, onChange: handleChange('message'), className: inputClass + ' mb-6 resize-none' }),
    status.error ? React.createElement('p', { className: 'text-red-400 text-sm mb-4' }, status.error) : null,
    React.createElement('button', {
      type: 'submit',
      disabled: status.loading,
      'data-testid': 'distributor-submit',
      className: 'w-full bg-[#d4af37] text-black font-semibold py-4 rounded-full hover:bg-white transition-all disabled:opacity-60'
    }, status.loading ? t('distFormSending') : t('distFormSubmit'))
  );
}

function DistributorsPage() {
  var t = useTranslation().t;
  var cards = [];
  for (var i = 0; i < distributors.length; i++) {
    cards.push(React.createElement(DistributorCard, { key: i, distributor: distributors[i] }));
  }

  return React.createElement(Layout, null,
    React.createElement(SEO, { title: PAGE_SEO['distributors'].title, description: PAGE_SEO['distributors'].description, url: '/distributors' }),
    React.createElement('section', { className: 'pt-32 pb-20 bg-gradient-to-b from-neutral-900 to-black' },
      React.createElement('div', { className: 'max-w-7xl mx-auto px-6 lg:px-12 text-center' },
        React.createElement('p', { className: 'text-sm tracking-widest uppercase text-[#d4af37] mb-4' }, t('distributorsWorldwide')),
        React.createElement('h1', { className: 'text-5xl md:text-7xl font-bold text-white mb-6' }, t('distributorsTitle')),
        React.createElement('p', { className: 'text-xl text-neutral-400 max-w-2xl mx-auto' }, t('distributorsSubtitle'))
      )
    ),
    React.createElement('section', { 'data-testid': 'distributors-section', className: 'py-16 bg-black' },
      React.createElement('div', { className: 'max-w-4xl mx-auto px-6 lg:px-12' },
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-6 mb-20' }, cards),
        React.createElement('div', { className: 'border-t border-white/10 pt-16' },
          React.createElement('div', { className: 'text-center mb-10' },
            React.createElement('h2', { className: 'text-3xl md:text-4xl font-bold text-white mb-3' }, t('becomeDistributor')),
            React.createElement('p', { className: 'text-neutral-400 max-w-lg mx-auto' }, t('becomeDistributorIntro'))
          ),
          React.createElement(DistributorForm, null)
        )
      )
    )
  );
}

export default DistributorsPage;
